import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input'
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';
import { enablePWAPersistence, isPWAMode } from '../../lib/pwa-session';
import { Turnstile } from '@marsidev/react-turnstile';
import { env, isCaptchaDisabled } from '../../config/env';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // 🎯 DEBUG: Log CAPTCHA status on component mount
  useEffect(() => {
    const captchaDisabled = isCaptchaDisabled();
    console.log('🔍 Login Component - CAPTCHA Status:', {
      disabled: captchaDisabled,
      envVar: import.meta.env.VITE_DISABLE_CAPTCHA,
      localStorage: typeof window !== 'undefined' ? localStorage.getItem('nosh_disable_captcha') : 'N/A',
      isProduction: env.IS_PRODUCTION
    });
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle email verification when user comes from verification link
  useEffect(() => {
    const handleEmailVerification = async () => {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // 🔧 OAUTH CANCELLATION DETECTION: Check for OAuth cancellation first
      if (error === 'access_denied' || 
          (error && errorDescription && errorDescription.toLowerCase().includes('cancel'))) {
        
        console.log('🚫 OAuth cancellation detected:', {
          error,
          errorDescription,
          action: 'staying on login page'
        });

        // Show toast notification for cancellation
        toast({
          title: "Google authentication cancelled",
          description: "You cancelled the Google sign-in process. You can try again or sign in with email instead.",
          variant: "default",
        });

        // Clean up URL parameters but STAY on login page
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.pathname);
        return;
      }

      // Note: OAuth success now redirects directly to Dashboard
      // OAuth success detection has been moved to Dashboard.tsx

      // Check if this is an email verification link
      const isVerificationLink = tokenHash || type === 'email';
      
      if (!isVerificationLink) {
        return; // Not a verification link, proceed normally
      }

      console.log('📧 Email verification detected in login page:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasTokenHash: !!tokenHash,
        type,
        error,
        errorDescription
      });

      // Check for errors first (excluding OAuth cancellation which is handled above)
      if (error && error !== 'access_denied') {
        console.error('❌ Email verification error:', error, errorDescription);
        toast({
          title: "Verification failed",
          description: errorDescription || "There was an error verifying your email. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setIsVerifying(true);

      try {
        // Handle verification with access/refresh tokens (OAuth-style flow)
        if (accessToken && refreshToken) {
          // Setting session with tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) throw error;

                      // Email verification successful
          setIsEmailVerified(true);
          setIsVerifying(false);
          
          toast({
            title: "Email verified successfully! ✨",
            description: "You're now logged in and can access all features.",
          });

          // Auto-redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
          return;
        }
        // Handle verification with token_hash (OTP-style flow)
        else if (tokenHash && type === 'email') {
                      // Verifying OTP
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'email'
          });

          if (error) throw error;

          console.log('✅ Email verification successful via OTP');
          setIsEmailVerified(true);
          setIsVerifying(false);

          // Check if user is now signed in automatically
          if (data.session) {
            toast({
              title: "Email verified successfully! ✨",
              description: "You're now logged in and can access all features.",
            });

            // Auto-redirect to dashboard after 2 seconds
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            // Email verified but not signed in - show success message
            toast({
              title: "Email verified successfully! ✨",
              description: "Please sign in below to access your account.",
            });
          }
          return;
        }
      } catch (error: any) {
        console.error('❌ Error during email verification:', error);
        setIsVerifying(false);
        
        let errorMessage = "There was an error verifying your email. Please try signing in manually.";
        if (error.message?.includes('expired') || error.message?.includes('invalid')) {
          errorMessage = "This verification link has expired or is invalid. Please request a new verification email.";
        }
        
        toast({
          title: "Verification failed",
          description: errorMessage,
          variant: "destructive",
        });
      }

      setIsVerifying(false);
    };

    handleEmailVerification();
  }, [searchParams, navigate, toast]);

  // For 3D card effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [10, -10]);
  const rotateY = useTransform(mouseX, [-300, 300], [-10, 10]);

  const handleMouseMove = (e: React.MouseEvent) => {
    // Disable tilt effect on mobile
    if (isMobile) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    // Disable tilt effect on mobile
    if (isMobile) return;
    
    mouseX.set(0);
    mouseY.set(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 🎯 HACKATHON FEATURE: Skip CAPTCHA validation if disabled
    if (!isCaptchaDisabled() && !turnstileToken) {
      toast({
        title: "Security verification required",
        description: "Please complete the security check to continue.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          // 🎯 HACKATHON FEATURE: Only include captchaToken if CAPTCHA is enabled
          ...(isCaptchaDisabled() ? {} : { captchaToken: turnstileToken || undefined }),
        },
      });

      if (error) {
        throw error;
      }

      // Enable PWA session persistence
      enablePWAPersistence(rememberMe);
      
      // Log session info for debugging
      if (isPWAMode()) {
        console.log('📱 PWA login detected - enabling permanent session');
      } else if (rememberMe) {
        console.log('🔐 Remember me enabled - extending session duration');
      }

      // Successful login
      toast({
        title: "Welcome back!",
        description: "Successfully signed in to your account.",
      });

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      
      let errorMessage = 'Failed to sign in';
      
      if (error.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.message?.includes('ERR_NAME_NOT_RESOLVED') || error.name === 'AbortError') {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error signing in",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        throw error;
      }

    } catch (error: any) {
      toast({
        title: "Error signing in with Google",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div 
      className="w-screen bg-background relative overflow-hidden flex items-center justify-center" 
      style={{ 
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Background gradient effect - using consistent app background */}
      <div 
        className="fixed inset-0 bg-background"
      />
      
      {/* Subtle decorative elements */}
      <div 
        className="fixed top-0 right-0 w-96 h-96 rounded-full blur-[100px] opacity-20"
        style={{
          background: 'radial-gradient(circle, hsla(342, 90%, 85%, 0.4), transparent)'
        }}
      />
      
      <div 
        className="fixed bottom-0 left-0 w-80 h-80 rounded-full blur-[120px] opacity-15"
        style={{
          background: 'radial-gradient(circle, hsla(342, 70%, 88%, 0.3), transparent)'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-sm px-4 relative z-10"
        style={{ perspective: 1500 }}
      >
        <motion.div
          className="relative"
          style={isMobile ? {} : { rotateX, rotateY }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          whileHover={isMobile ? {} : { z: 10 }}
        >
          <div className="relative group">
            {/* Card glow effect */}
            <motion.div 
              className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"
              animate={{
                boxShadow: [
                  "0 0 10px 2px rgba(255,255,255,0.03)",
                  "0 0 15px 5px rgba(255,255,255,0.05)",
                  "0 0 10px 2px rgba(255,255,255,0.03)"
                ],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut", 
                repeatType: "mirror" 
              }}
            />

            {/* White card background - matching app design */}
            <div className="relative bg-card backdrop-blur-xl rounded-2xl p-6 border border-border shadow-2xl overflow-hidden">
              {/* Logo and header */}
              <div className="text-center space-y-2 mb-6">
                <a href="https://noshhq.com" className="inline-block">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", duration: 0.8 }}
                    className="mx-auto w-10 h-10 rounded-full border border-primary/20 bg-primary/5 flex items-center justify-center relative overflow-hidden"
                  >
                    <span className="text-lg font-bold text-primary">N</span>
                  </motion.div>
                </a>

                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl font-bold text-foreground"
                >
                  Welcome Back
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-muted-foreground text-xs"
                >
                  {isEmailVerified ? 'Your email is verified! Welcome to Nosh.' : isVerifying ? 'Verifying your email...' : 'Sign in to continue to Nosh'}
                </motion.p>
              </div>

              {/* Email Verification Banner */}
              <AnimatePresence>
                {(isEmailVerified || isVerifying) && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="mb-6 overflow-hidden"
                  >
                    <div className={`p-4 rounded-xl border ${
                      isVerifying 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    }`}>
                      <div className="flex items-center gap-3">
                        {isVerifying ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            className="flex-shrink-0"
                          >
                            <Mail className="w-5 h-5 text-blue-600" />
                          </motion.div>
                        ) : (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 10 }}
                            className="flex-shrink-0"
                          >
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </motion.div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-medium ${
                              isVerifying 
                                ? 'text-blue-800 dark:text-blue-200' 
                                : 'text-green-800 dark:text-green-200'
                            }`}>
                              {isVerifying ? 'Verifying your email...' : 'Email verified successfully!'}
                            </p>
                            {isEmailVerified && (
                              <Sparkles className="w-4 h-4 text-yellow-500" />
                            )}
                          </div>
                          <p className={`text-xs mt-1 ${
                            isVerifying 
                              ? 'text-blue-600 dark:text-blue-300' 
                              : 'text-green-600 dark:text-green-300'
                          }`}>
                            {isVerifying 
                              ? 'Please wait while we confirm your email address.' 
                              : 'Thank you for confirming your email. You can now sign in below.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Login form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Google Sign In */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full relative group/google"
                >
                  <div className="relative overflow-hidden bg-background text-foreground font-medium h-10 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 flex items-center justify-center gap-2">
                    <img 
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                      alt="Google" 
                      className="w-4 h-4"
                    />
                    <span className="text-foreground transition-colors text-xs">
                      Sign in with Google
                    </span>
                  </div>
                </motion.button>

                {/* Divider */}
                <div className="relative mt-2 mb-5 flex items-center">
                  <div className="flex-grow border-t border-border"></div>
                  <span className="mx-3 text-xs text-muted-foreground">
                    or
                  </span>
                  <div className="flex-grow border-t border-border"></div>
                </div>

                <motion.div className="space-y-3">
                  {/* Email input */}
                  <motion.div 
                    className={`relative ${focusedInput === "email" ? 'z-10' : ''}`}
                    whileFocus={{ scale: 1.02 }}
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  >
                    <div className="relative flex items-center overflow-hidden rounded-lg">
                      <Mail className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                        focusedInput === "email" ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocusedInput("email")}
                        onBlur={() => setFocusedInput(null)}
                        className="w-full bg-input border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-10 transition-all duration-300 pl-10 pr-3 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </motion.div>

                  {/* Password input */}
                  <motion.div 
                    className={`relative ${focusedInput === "password" ? 'z-10' : ''}`}
                    whileFocus={{ scale: 1.02 }}
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  >
                    <div className="relative flex items-center overflow-hidden rounded-lg">
                      <Lock className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                        focusedInput === "password" ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocusedInput("password")}
                        onBlur={() => setFocusedInput(null)}
                        className="w-full bg-input border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-10 transition-all duration-300 pl-10 pr-10 focus:ring-2 focus:ring-primary/20"
                      />
                      
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)} 
                        className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Remember me & Forgot password */}
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center space-x-2">
                    <div className="relative flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={() => setRememberMe(!rememberMe)}
                        className="appearance-none h-4 w-4 rounded border border-border bg-background checked:bg-primary checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        style={{ minHeight: 'auto', minWidth: 'auto' }}
                      />
                      {rememberMe && (
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="absolute inset-0 flex items-center justify-center text-primary-foreground pointer-events-none"
                        >
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M9 1L3.5 6.5L1 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </motion.div>
                      )}
                    </div>
                    <label htmlFor="remember-me" className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 leading-none">
                      Remember me
                    </label>
                  </div>
                  
                  <Link to="/auth/forgot-password" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-200">
                    Forgot password?
                  </Link>
                </div>

                {/* 🎯 HACKATHON FEATURE: Conditionally render Turnstile Captcha */}
                {!isCaptchaDisabled() && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex justify-center pt-2"
                  >
                    <Turnstile
                      siteKey={env.TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onError={() => {
                        setTurnstileToken(null);
                        toast({
                          title: "Security verification failed",
                          description: "Please try again or refresh the page.",
                          variant: "destructive",
                        });
                      }}
                      onExpire={() => setTurnstileToken(null)}
                      options={{
                        theme: 'light'
                      }}
                    />
                  </motion.div>
                )}



                {/* Sign in button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-10 mt-5"
                >
                  <AnimatePresence mode="wait">
                    {isLoading ? (
                      <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center"
                      >
                        <div className="w-4 h-4 border-2 border-primary-foreground/70 border-t-transparent rounded-full animate-spin" />
                      </motion.div>
                    ) : (
                      <motion.span
                        key="button-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center gap-2"
                      >
                        Sign In
                        <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>



                {/* Sign up link */}
                <motion.p 
                  className="text-center text-xs text-muted-foreground mt-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  Don't have an account?{' '}
                  <Link 
                    to="/auth/signup" 
                    className="relative inline-block group/signup"
                  >
                    <span className="relative z-10 text-primary group-hover/signup:text-primary/80 transition-colors duration-300 font-medium">
                      Sign up
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover/signup:w-full transition-all duration-300" />
                  </Link>
                </motion.p>
              </form>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;